// src/pages/Checkout.jsx  – currency comes from router state
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Checkout() {
  const { state } = useLocation();        // { planId, label, currency }
  const navigate = useNavigate();
  const [stage, setStage] = useState('loading'); // 'loading' | 'error'

  const API = process.env.REACT_APP_API_URL;
  const RZP_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;
  const token = localStorage.getItem('kodanzaToken');

  // 1️⃣ bail out if user landed here directly
  useEffect(() => {
    if (!state?.planId || !state?.currency) {
      navigate('/pricing');
    }
  }, [state, navigate]);

  // 2️⃣ Razorpay order flow
  useEffect(() => {
    if (!state?.planId || !state?.currency) return;

    const loadScript = () =>
      new Promise((resolve) => {
        if (document.getElementById('razorpay-checkout')) return resolve(true);
        const s = document.createElement('script');
        s.id = 'razorpay-checkout';
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
      });

    (async () => {
      try {
        /* create order */
        const orderRes = await fetch(`${API}/api/payments/razorpay/order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            courseId: state.planId,
            currency: state.currency,
          }),
        });
        if (!orderRes.ok) throw new Error(`Order API → ${orderRes.status}`);
        const { orderId, amount, currency } = await orderRes.json();

        /* load SDK */
        if (!(await loadScript())) throw new Error('Razorpay SDK failed');

        /* open checkout */
        const rz = new window.Razorpay({
          key: RZP_KEY,
          order_id: orderId,
          amount,
          currency,
          name: 'Kodanza Courses',
          description: state.label,
          theme: { color: '#6366f1' },
          handler: async (resp) => {
            const vRes = await fetch(`${API}/api/payments/razorpay/verify`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                ...resp,
                courseId: state?.planId,
              }),
            });
            if (vRes.ok) return navigate('/payment-success');
            setStage('error');
          },
          modal: { ondismiss: () => navigate('/payment-failure') },
        });
        setStage('opened');
        rz.open();
      } catch (err) {
        console.error(err);
        setStage('error');
      }
    })();
  }, []);

  /* ---------- UI ---------- */
  const message =
    stage === 'loading'
      ? 'Preparing payment…'
      : 'Oops! Something broke. Please try again or contact support.';

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="w-full max-w-lg bg-white p-10 rounded-2xl shadow-lg">
        <h2 className="text-2xl font-extrabold text-center mb-8">
          Checkout {state?.label ? `– ${state.label}` : ''}
        </h2>
        <p className="text-center">{message}</p>
        {stage === 'error' && (
          <button
            className="mt-8 w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg"
            onClick={() => navigate('/pricing')}
          >
            Back to Pricing
          </button>
        )}
      </div>
    </main>
  );
}
